import { getDownloads } from "../api/downloads";
import { getBoards } from "../api/boards";
import { getAccessories } from "../api/metals";
import { getMetalFrames } from "../api/metalFrames";
import { getPublicSolutions } from "../api/solutions";
import { resolveMediaUrl } from "./media";
import { mapPublicProductPdfs, pickPublicPdfArray } from "./productPdfs";

const normalizeLang = (value) => String(value || "en").toLowerCase().split("-")[0];

const unwrapList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.items)) return data.items;
  }
  return [];
};

/**
 * Same merged document list as the /downloads page (DownloadsDownloads):
 * CMS downloads + boards + accessories + metal frames + solutions PDFs.
 * Solution-derived rows use `source: "solutions"` so UIs can filter them.
 */
export async function fetchAllDownloadsDocuments(language) {
  const lang = normalizeLang(language);
  const [downloadsData, boardsData, accessoriesData, metalFramesData, solutionsData] =
    await Promise.all([
      getDownloads({ language: lang }),
      getBoards({ language: lang }),
      getAccessories({ language: lang }),
      getMetalFrames({ language: lang }),
      getPublicSolutions(),
    ]);

  const staticDownloads = (Array.isArray(downloadsData) ? downloadsData : [])
    .map((item) => {
      const fileUrl = resolveMediaUrl(item?.fileUrl);
      if (!fileUrl) return null;
      return {
        id: `downloads-${item?.id ?? item?.title ?? item?.name ?? Math.random()}`,
        title: item?.title || "Untitled PDF",
        uploadedAt: "",
        type: "PDF",
        fileUrl,
        source: "other",
      };
    })
    .filter(Boolean);

  const boardsPdfs = mapPublicProductPdfs(
    pickPublicPdfArray(...unwrapList(boardsData))
  ).map((pdf, idx) => ({
    id: `boards-${pdf?.id ?? idx}`,
    title: pdf?.title || "Untitled PDF",
    uploadedAt: "",
    type: "PDF",
    fileUrl: pdf?.href || "",
    source: "boards",
  }));

  const accessoriesPdfs = mapPublicProductPdfs(
    pickPublicPdfArray(...unwrapList(accessoriesData))
  ).map((pdf, idx) => ({
    id: `accessories-${pdf?.id ?? idx}`,
    title: pdf?.title || "Untitled PDF",
    uploadedAt: "",
    type: "PDF",
    fileUrl: pdf?.href || "",
    source: "accessories",
  }));

  const metalFramesPdfs = mapPublicProductPdfs(
    pickPublicPdfArray(...unwrapList(metalFramesData))
  ).map((pdf, idx) => ({
    id: `metals-${pdf?.id ?? idx}`,
    title: pdf?.title || "Untitled PDF",
    uploadedAt: "",
    type: "PDF",
    fileUrl: pdf?.href || "",
    source: "metals",
  }));

  const solutionsPdfs = mapPublicProductPdfs(
    pickPublicPdfArray(...unwrapList(solutionsData))
  ).map((pdf, idx) => ({
    id: `solutions-${pdf?.id ?? idx}`,
    title: pdf?.title || "Untitled PDF",
    uploadedAt: "",
    type: "PDF",
    fileUrl: pdf?.href || "",
    source: "solutions",
  }));

  const byUrl = new Map();
  [...staticDownloads, ...boardsPdfs, ...accessoriesPdfs, ...metalFramesPdfs, ...solutionsPdfs].forEach((doc) => {
    const key = String(doc?.fileUrl || "").trim();
    if (!key) return;
    if (!byUrl.has(key)) byUrl.set(key, doc);
  });

  return [...byUrl.values()];
}
